import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Chip,
  useTheme,
} from '@mui/material';
import {
  AddCircle as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import { eventService, StoreEvent, CreateStoreEventInput } from '../../../services/eventService';
import { useNotification } from '../../../contexts/NotificationContext';

const EventManagementTab: React.FC = () => {
  const theme = useTheme();
  const { showNotification } = useNotification();
  
  const [events, setEvents] = useState<StoreEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  // Form State
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [location, setLocation] = useState('');
  const [type, setType] = useState<'event' | 'tournament'>('event');
  const [entryFee, setEntryFee] = useState('');
  const [prizes, setPrizes] = useState('');

  const loadEvents = async () => {
    setLoading(true);
    try {
      const data = await eventService.getAllEvents();
      setEvents(data);
    } catch (error) {
      showNotification('Failed to load events', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  const openNewModal = () => {
    setEditingId(null);
    setTitle('');
    setDescription('');
    setDate('');
    setTime('');
    setLocation('');
    setType('event');
    setEntryFee('');
    setPrizes('');
    setIsModalOpen(true);
  };

  const openEditModal = (event: StoreEvent) => {
    setEditingId(event.id);
    setTitle(event.title);
    setDescription(event.description);
    setDate(event.date);
    setTime(event.time);
    setLocation(event.location);
    setType(event.type);
    setEntryFee(event.entryFee || '');
    setPrizes(event.prizes || '');
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await eventService.deleteEvent(id);
        showNotification('Event deleted successfully', 'success');
        loadEvents();
      } catch (error) {
        showNotification('Failed to delete event', 'error');
      }
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    
    const payload: CreateStoreEventInput = {
      title,
      description,
      date,
      time,
      location,
      type,
      entryFee: entryFee || undefined,
      prizes: prizes || undefined,
    };

    try {
      if (editingId) {
        await eventService.updateEvent(editingId, payload);
        showNotification('Event updated successfully', 'success');
      } else {
        await eventService.createEvent(payload);
        showNotification('Event created successfully', 'success');
      }
      setIsModalOpen(false);
      loadEvents();
    } catch (error) {
      showNotification('Failed to save event', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight="bold">
          Events & Tournaments Management
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNewModal}>
          Create Event
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ background: 'rgba(255,255,255,0.02)' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Date & Time</TableCell>
              <TableCell>Location</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No events found.
                </TableCell>
              </TableRow>
            ) : (
              events.map((evt) => (
                <TableRow key={evt.id}>
                  <TableCell sx={{ fontWeight: 'bold' }}>{evt.title}</TableCell>
                  <TableCell>
                    <Chip 
                      icon={evt.type === 'tournament' ? <TrophyIcon /> : <EventIcon />} 
                      label={evt.type.toUpperCase()} 
                      color={evt.type === 'tournament' ? 'warning' : 'primary'} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell>{evt.date} @ {evt.time}</TableCell>
                  <TableCell>{evt.location}</TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => openEditModal(evt)} color="primary">
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(evt.id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleSave}>
          <DialogTitle>{editingId ? 'Edit Event' : 'Create New Event'}</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                select
                label="Type"
                value={type}
                onChange={(e) => setType(e.target.value as 'event' | 'tournament')}
                fullWidth
                required
              >
                <MenuItem value="event">Casual Event</MenuItem>
                <MenuItem value="tournament">Competitive Tournament</MenuItem>
              </TextField>

              <TextField
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                required
              />
              
              <TextField
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                required
                multiline
                rows={3}
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Time"
                  placeholder="e.g. 6:00 PM"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  fullWidth
                  required
                />
              </Box>

              <TextField
                label="Location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                fullWidth
                required
              />

              <TextField
                label="Entry Fee (Optional)"
                placeholder="e.g. $10 or Free"
                value={entryFee}
                onChange={(e) => setEntryFee(e.target.value)}
                fullWidth
              />

              {type === 'tournament' && (
                <TextField
                  label="Prizes (Optional)"
                  placeholder="e.g. 1st Place: Booster Box"
                  value={prizes}
                  onChange={(e) => setPrizes(e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default EventManagementTab;
